/** Static copy from Audit Practice Manual (2024 Edition).xlsm — sheets Si / Ss / S2 / S3 */

import type {G0Page, GProgrammeRow} from '@/pages/components/audit-program/g-section/constants';
import type {ESummaryObjective, ESummaryPage} from '@/pages/components/audit-program/e-section/constants';
import type {SectionIndexRow} from '@/pages/components/audit-program/SectionIndexView';

export type SSectionPageCode = 'Si' | 'Ss' | 'S2' | 'S3';

export const S_SECTION_PAGES: SSectionPageCode[] = ['Si', 'Ss', 'S2', 'S3'];

export const S_PAGE_TITLES: Record<SSectionPageCode, string> = {
    Si: 'Index',
    Ss: 'Summary sheet - Operating effectiveness of controls',
    S2: 'Audit programme - Operating effectiveness of controls',
    S3: 'Internal Control Evaluation',
};

export type SSectionIndexRow = SectionIndexRow & {
    pageCode?: Exclude<SSectionPageCode, 'Si'>;
};

export const S_SECTION_INDEX_LETTER = 'S';
export const S_SECTION_INDEX_TITLE = 'OPERATING EFFECTIVENESS OF CONTROLS';

/** TOC from APM Si sheet; navigable rows point to implemented Program pages. */
export const S_SECTION_INDEX: SSectionIndexRow[] = [
    {id: 's-1', kind: 'item', num: '1', title: ''},
    {id: 's-2', kind: 'item', num: '2', title: 'Audit programme', pageCode: 'S2'},
    {id: 's-3', kind: 'item', num: '3', title: 'Internal Control Evaluation', pageCode: 'S3'},
    {id: 's-s', kind: 'item', num: 's', title: 'Summary sheet', pageCode: 'Ss'},
    {id: 's-4', kind: 'item', num: '4', title: ''},
    {id: 's-5', kind: 'item', num: '5', title: ''},
];

export type SProgrammePage = G0Page;
export type SProgrammeRow = GProgrammeRow;
export type SSummaryObjective = ESummaryObjective;
export type SSummaryPage = ESummaryPage;

export const SS_PAGE: SSummaryPage = {
    title: 'SUMMARY SHEET – OPERATING EFFECTIVENESS OF CONTROLS',
    objectivesTitle: 'Audit objectives',
    objectives: [
        {
            id: 'obj1',
            num: '1',
            text: 'To test the effective operation of key controls where the risk assessment includes an expectation that they are operating effectively.',
            defaultKeyTests: '1, 2, 3',
            defaultSuppRef: '',
        },
        {
            id: 'obj2',
            num: '2',
            text: 'To test the effective operation of key controls where substantive tests alone do not provide sufficient evidence of operation.',
            defaultKeyTests: '1, 2, 3',
            defaultSuppRef: '',
        },
        {
            id: 'obj3',
            num: '3',
            text: 'To seek to reduce the nature or extent of substantive testing where reliance on controls is more effective.',
            defaultKeyTests: '1, 2, 3',
            defaultSuppRef: '',
        },
        {
            id: 'obj4',
            num: '4',
            text: 'To ensure that reliance on the testing of controls at the interim stage or in previous audits is only relied upon where it is appropriate to do so.',
            defaultKeyTests: '4, 5, 6, 7, 8, 9',
            defaultSuppRef: '',
        },
        {
            id: 'obj5',
            num: '5',
            text: 'To identify weaknesses in internal controls that should be brought to the attention of management.',
            defaultKeyTests: '10, 11',
            defaultSuppRef: '',
        },
    ],
    planningTitle: 'Planning conclusion',
    finalTitle: 'Final conclusion',
    materialityLabel: 'Materiality (HK$)',
    planningNote:
        'I am satisfied that from the tests planned sufficient appropriate evidence can be gained to satisfy the objectives.',
    finalBullets: [
        '*(subject to the matters described below and highlighted on B6 or B9) in my opinion such procedures provide additional appropriate audit evidence as to the completeness, accuracy and validity of information in the accounts.',
    ],
};

export const S2_PAGE: SProgrammePage = {
    title: 'AUDIT PROGRAMME - OPERATING EFFECTIVENESS OF CONTROLS',
    rows: [
        {id: 'r0', kind: 'note', num: '', text: 'In this APM, a risk assessed as a high risk equates to a significant risk per the HKSAs.', level: 0},
        {id: 'r1', kind: 'section', num: '', text: 'General', level: 0},
        {id: 'r2', kind: 'main', num: '1', text: 'Testing the operating effectiveness of internal controls should be undertaken where:', level: 0},
        {id: 'r3', kind: 'sub', num: '(a)', text: 'the risk assessment includes an expectation that controls are operating effectively;', level: 1},
        {id: 'r4', kind: 'sub', num: '(b)', text: 'substantive tests alone do not provide sufficient evidence of operation;', level: 1},
        {id: 'r5', kind: 'sub', num: '(c)', text: 'this is more effective than relying solely on substantive procedures.', level: 1},
        {id: 'r6', kind: 'main', num: '2', text: 'Where it is planned to rely on controls over a high risk area confirm that the testing of those controls takes place in the current period.', level: 0},
        {id: 'r7', kind: 'main', num: '3', text: 'Document each control to be tested on the ICE (S3) and reference each to the working papers evidencing the testing of operating effectiveness of the controls. Consider:', level: 0},
        {id: 'r8', kind: 'sub', num: '(a)', text: 'How the controls were applied at relevant times during the period under audit.', level: 1},
        {id: 'r9', kind: 'sub', num: '(b)', text: 'The consistency with which they were applied.', level: 1},
        {id: 'r10', kind: 'sub', num: '(c)', text: 'By whom or by what means they were applied.', level: 1},
        {id: 'r11', kind: 'note', num: '', text: 'Note. Inquiry alone is not sufficient to test the operating effectiveness of controls.', level: 1},
        {id: 'r12', kind: 'section', num: '', text: 'Interim testing', level: 0},
        {id: 'r13', kind: 'main', num: '4', text: 'Where the operating effectiveness of controls was tested during an interim period:', level: 0},
        {id: 'r14', kind: 'sub', num: '(a)', text: 'Obtain audit evidence about significant changes to those controls subsequent to the interim period;', level: 1},
        {id: 'r15', kind: 'sub', num: '(b)', text: 'Determine the additional audit evidence to be obtained for the remaining period.', level: 1},
        {id: 'r16', kind: 'section', num: '', text: 'Reliance on testing in prior years', level: 0},
        {id: 'r17', kind: 'main', num: '5', text: 'Determine whether it is appropriate to use audit evidence about the operating effectiveness of controls obtained in previous audits. Consider:', level: 0},
        {id: 'r18', kind: 'sub', num: '(a)', text: 'The effectiveness of other elements of internal control, including the control environment, the entity\'s monitoring of controls, and the entity\'s risk assessment process;', level: 1},
        {id: 'r19', kind: 'sub', num: '(b)', text: 'The risks arising from the characteristics of the control, including whether it is manual or automated;', level: 1},
        {id: 'r20', kind: 'sub', num: '(c)', text: 'The effectiveness of general IT-controls;', level: 1},
        {id: 'r21', kind: 'sub', num: '(d)', text: 'The effectiveness of the control and its application by the entity, including the nature and extent of deviations in the application of the control noted in previous audits, and whether there have been personnel changes that significantly affect the application of the control;', level: 1},
        {id: 'r22', kind: 'sub', num: '(e)', text: 'Whether the lack of a change in a particular control poses a risk due to changing circumstances; and', level: 1},
        {id: 'r23', kind: 'sub', num: '(f)', text: 'The risks of material misstatement and the extent of reliance on the control.', level: 1},
        {id: 'r24', kind: 'main', num: '6', text: 'Where it is planned to rely on evidence about the operating effectiveness of controls obtained in prior audits, has evidence about whether those specific controls have subsequently changed been obtained?', level: 0},
        {id: 'r25', kind: 'main', num: '7', text: 'Where it is planned to place reliance on controls that have changed since they were last tested has the operating effectiveness of those controls been tested this year?', level: 0},
        {id: 'r26', kind: 'main', num: '8', text: 'Where it is planned to place reliance on controls that have not changed since they were last tested, confirm that those controls were last tested at least once in every third audit.', level: 0},
        {id: 'r27', kind: 'main', num: '9', text: 'Where it is planned to place reliance on a number of controls that have not changed since they were last tested, confirm that some of those controls were tested this year.', level: 0},
        {id: 'r28', kind: 'section', num: '', text: 'Failure of controls', level: 0},
        {id: 'r29', kind: 'main', num: '10', text: 'Consider whether the results of substantive testing indicate that controls may not be operating properly.', level: 0},
        {id: 'r30', kind: 'main', num: '11', text: 'Where deviations from controls are identified make specific inquiries to understand the extent and potential consequences of each.', level: 0},
        {id: 'r31', kind: 'section', num: '', text: 'Conclusion', level: 0},
        {id: 'r32', kind: 'main', num: '12', text: 'Consider whether:', level: 0},
        {id: 'r33', kind: 'sub', num: '(a)', text: 'It is appropriate to place the planned reliance on the operating effectiveness of internal controls tested in the current period.', level: 1},
        {id: 'r34', kind: 'sub', num: '(b)', text: 'It is appropriate to place the planned reliance on the operating effectiveness of internal controls tested in previous periods.', level: 1},
        {id: 'r35', kind: 'sub', num: '(c)', text: 'There are any points which need to be included in a letter of representation or letter of comment and record on A5 or A6 as appropriate.', level: 1},
        {id: 'r36', kind: 'sub', num: '(d)', text: 'Any weaknesses in control identified constitute significant deficiencies.', level: 1},
        {id: 'r37', kind: 'sub', num: '(e)', text: 'Additional audit procedures are required.', level: 1},
    ],
};

export type S3Column = {id: string; label: string};

export type S3Page = {
    title: string;
    blankRows: number;
    columns: S3Column[];
};

/** S3 — Internal Control Evaluation (blank working table). */
export const S3_PAGE: S3Page = {
    title: 'INTERNAL CONTROL EVALUATION',
    blankRows: 12,
    columns: [
        {id: 'ref', label: 'Ref'},
        {id: 'businessArea', label: 'Business area'},
        {id: 'keyControl', label: 'Key Control'},
        {
            id: 'test',
            label: 'Test of Operating Effectiveness (inquiry alone is not sufficient)',
        },
        {id: 'wpRef', label: 'Ref. to detailed work'},
    ],
};
